import { Textbox, FabricText, Canvas as FabricCanvas } from 'fabric';
import type { TextElement, ButtonElement, CanvasElement } from '../types';
import { isVariantReady } from './fontLoader';
import { measureTextBlockHeight } from './textMetrics';
import { useStudioStore } from '../stores/studioStore';

export const ELEMENT_ID_KEY = '_csElementId';
export const ARTBOARD_PADDING = 60;

/**
 * Calculate minimum width needed to display text content without blank space.
 * SINGLE SOURCE OF TRUTH for text width validation.
 * Used by: object:modified, createFabricObjectFromElement, syncElementsToCanvas,
 *          clampTextboxWidth, auditTextWidths.
 * DO NOT REMOVE — this bug has regressed 3+ times.
 */
/**
 * INVARIANT: width is measured in advance-width units, NEVER ink-rect units.
 *
 * Fabric's Textbox is a left-origin container: it paints glyphs starting at
 * textbox.left and advances rightward using advance-width metrics. The caret
 * renders at the glyph's advance-width x. Any attempt to substitute ink-rect
 * width (actualBoundingBoxLeft/Right) desyncs cursor position from container
 * bounds — typing in the box shows dead space on one side and clipped glyphs
 * on the other. Do NOT reintroduce ink-rect measurement here.
 */
export function measureAdvanceLineWidth(ctx: CanvasRenderingContext2D, line: string): number {
  const text = line || 'M';
  return ctx.measureText(text).width;
}

export function getTextMinWidth(textObj: Textbox | FabricText): number {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const fontStyle = (textObj as any).fontStyle || 'normal';
      const fontWeight = (textObj as any).fontWeight || 'normal';
      const fontSize = (textObj as any).fontSize || 24;
      const fontFamily = (textObj as any).fontFamily || 'Arial';
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      const text = (textObj as any).text || '';
      const textLines = text.split('\n');
      let maxWidth = 0;
      for (const line of textLines) {
        const w = measureAdvanceLineWidth(ctx, line);
        if (w > maxWidth) maxWidth = w;
      }
      if (maxWidth > 0) return maxWidth;
    }
  } catch {
    // ignore
  }

  if (typeof (textObj as any)._measureLine === 'function' && (textObj as any)._textLines) {
    let maxLineWidth = 0;
    const lines = (textObj as any)._textLines as unknown[];
    for (let i = 0; i < lines.length; i++) {
      try {
        const lineInfo = (textObj as any)._measureLine(i);
        if (lineInfo && lineInfo.width > maxLineWidth) {
          maxLineWidth = lineInfo.width;
        }
      } catch {
        break;
      }
    }
    if (maxLineWidth > 0) return maxLineWidth;
  }

  const charCount = ((textObj as any).text || '').length || 1;
  const fontSize = (textObj as any).fontSize || 24;
  return charCount * fontSize * 0.6;
}

export function getTextMinWidthFromParams(text: string, fontSize: number, fontFamily: string, fontWeight: string | number = 'normal', fontStyle: string = 'normal'): number {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      const textLines = text.split('\n');
      let maxWidth = 0;
      for (const line of textLines) {
        const w = measureAdvanceLineWidth(ctx, line);
        if (w > maxWidth) maxWidth = w;
      }
      return Math.max(maxWidth, fontSize * 2);
    }
  } catch {
    // ignore
  }
  const charCount = text.length || 1;
  return Math.max(charCount * fontSize * 0.6, fontSize * 2);
}

/**
 * Apply the width policy for a textbox based on its widthIntent.
 *
 *   'auto'   - snap width to rendered text + 10% padding (can shrink OR grow).
 *   'manual' - floor only: never narrower than rendered text, never silently shrunk.
 *
 * Gated on isFontReady(): if the font has not painted, measurement uses a fallback
 * font with wider glyphs and produces an incorrect minWidth. We bail and let the
 * font-settle callback re-run the policy.
 *
 * Returns true if the width was changed.
 */
export type TextWidthIntent = 'auto' | 'manual';

export function applyTextboxWidthPolicy(
  textObj: Textbox | FabricText,
  intent: TextWidthIntent
): boolean {
  const family = (textObj as any).fontFamily as string | undefined;
  const weight = (textObj as any).fontWeight as string | number | undefined;
  const style = (textObj as any).fontStyle as string | undefined;
  // Variant-aware gate: isFontReady(family) returns true the moment ANY
  // variant of the family has painted, so measurement after a weight/style
  // swap ran against the wrong face and produced extra dead space or right-
  // edge clipping. Require the specific triple to be ready before measuring.
  if (family && !isVariantReady(family, weight, style)) return false;

  const minWidth = getTextMinWidth(textObj);
  if (minWidth <= 0) return false;

  // Width target = advance-width + small epsilon to avoid sub-pixel clip at
  // the trailing glyph. Advance-width already contains the last glyph's
  // trailing bearing; do NOT add additional style-dependent padding here or
  // cursor/container alignment will drift.
  const fontSize = ((textObj as any).fontSize as number) || 16;
  let target = minWidth + Math.max(fontSize * 0.08, 3);

  // Trailing punctuation (. , ! ? ; : " ' )) has a narrow advance width, and
  // proportional fonts often paint the glyph slightly past its advance. With
  // only a 2px epsilon the last character was clipping on re-export. Add a
  // small punctuation bump so ink always sits fully inside the container.
  const rawText: string = String((textObj as any).text || '');
  const lastGlyph = rawText.trimEnd().slice(-1);
  if (lastGlyph && /[.,!?;:"')\]}]/.test(lastGlyph)) {
    target += fontSize * 0.15;
  }

  // Text outline (Fabric stroke) paints OUTSIDE the advance width, so it
  // clips at the right edge without a matching allowance.
  const strokeWidth = ((textObj as any).strokeWidth as number) || 0;
  if (strokeWidth > 0) {
    target += strokeWidth;
  }

  const current = textObj.width ?? 0;

  let nextWidth: number | null = null;
  if (intent === 'auto') {
    if (Math.abs(current - target) > 1) nextWidth = target;
  } else {
    if (current < target) nextWidth = target;
  }

  if (nextWidth == null) return false;

  textObj.set({ width: nextWidth });
  if (typeof (textObj as any).initDimensions === 'function') {
    (textObj as any).initDimensions();
  }
  textObj.setCoords();
  return true;
}

// Button width fit policy. Buttons are a Fabric Group([bg Rect, label Text]).
// The bg Rect width is locked at element.width. If the font variant finishes
// loading after the project is open and the glyph advances for the stored
// label end up wider than the saved width, the label clips at the pill edges.
// Text elements solve this via applyTextboxWidthPolicy; buttons lack an
// equivalent. This helper grows (never shrinks) the pill so the label always
// fits, and persists the new width back to the element store.
export function applyButtonAdvanceFit(
  group: import('fabric').FabricObject,
  btnEl: ButtonElement
): { changed: boolean; newWidth: number } {
  const bg = (group as unknown as { _csButtonBg?: import('fabric').Rect })._csButtonBg;
  const label = (group as unknown as { _csButtonText?: FabricText })._csButtonText;
  if (!bg || !label) return { changed: false, newWidth: btnEl.width };
  const family = (label as unknown as { fontFamily?: string }).fontFamily || btnEl.fontFamily;
  const weight = (label as unknown as { fontWeight?: number | string }).fontWeight ?? btnEl.fontWeight ?? 600;
  const style = (label as unknown as { fontStyle?: string }).fontStyle || btnEl.fontStyle || 'normal';
  if (!family || !isVariantReady(family, weight, style)) return { changed: false, newWidth: btnEl.width };
  const fontSize = ((label as unknown as { fontSize?: number }).fontSize) || btnEl.fontSize || 16;
  const text = String((label as unknown as { text?: string }).text || btnEl.text || '');
  let advance = 0;
  try {
    const c = document.createElement('canvas');
    const cctx = c.getContext('2d');
    if (cctx) {
      cctx.font = `${style} ${weight} ${fontSize}px "${family}"`;
      for (const line of text.split('\n')) {
        const w = cctx.measureText(line || 'M').width;
        if (w > advance) advance = w;
      }
    }
  } catch {
    return { changed: false, newWidth: btnEl.width };
  }
  if (advance <= 0) return { changed: false, newWidth: btnEl.width };
  const paddingX = typeof btnEl.paddingX === 'number' ? btnEl.paddingX : Math.max(fontSize * 0.75, 12);
  const borderW = btnEl.border?.width ?? 0;
  const lastGlyph = text.trimEnd().slice(-1);
  const punctBump = lastGlyph && /[.,!?;:"')\]}]/.test(lastGlyph) ? fontSize * 0.15 : 0;
  const minWidth = advance + paddingX * 2 + borderW * 2 + punctBump;
  const currentWidth = (bg as unknown as { width?: number }).width ?? btnEl.width ?? 0;
  if (currentWidth >= minWidth - 0.5) return { changed: false, newWidth: currentWidth };
  const newWidth = Math.ceil(minWidth);
  bg.set({ width: newWidth });
  if (typeof (bg as unknown as { setCoords?: () => void }).setCoords === 'function') {
    (bg as unknown as { setCoords: () => void }).setCoords();
  }
  group.setCoords();
  group.canvas?.requestRenderAll();
  return { changed: true, newWidth };
}

export function resolveTextIntent(el: TextElement | undefined): TextWidthIntent {
  if (!el) return 'manual';
  return el.widthIntent === 'auto' ? 'auto' : 'manual';
}

// Descender-aware replacement for `textbox.height`. Fabric returns the
// line-box height (numLines * fontSize * lineHeight) which omits the
// descender hang below the final baseline, so exports clip 'g', 'j',
// 'p', 'q', 'y'. We grow the height to include the real font descent
// and never shrink it.
export function getDescenderSafeHeight(textObj: Textbox | FabricText): number {
  const fabricH = (textObj.height as number | undefined) ?? 0;
  const family = (textObj as unknown as { fontFamily?: string }).fontFamily || 'Arial';
  const weight = (textObj as unknown as { fontWeight?: number | string }).fontWeight ?? 400;
  const style = (textObj as unknown as { fontStyle?: string }).fontStyle || 'normal';
  if (family && !isVariantReady(family, weight, style)) return fabricH;
  const fontSize = ((textObj as unknown as { fontSize?: number }).fontSize) || 16;
  const lineHeight = ((textObj as unknown as { lineHeight?: number }).lineHeight) ?? 1.16;
  const wrappedLines = ((textObj as unknown as { textLines?: string[] }).textLines) || [];
  const lines = wrappedLines.length > 0
    ? wrappedLines
    : String((textObj as unknown as { text?: string }).text || '').split('\n');
  const measured = measureTextBlockHeight({
    lines,
    fontSize,
    fontFamily: family,
    fontWeight: weight,
    fontStyle: style,
    lineHeight,
  });
  return Math.max(fabricH, measured.height);
}

// Fabric's Textbox has no native `textTransform`, so we transform the rendered
// string on the way in. The store always holds the raw text the user typed, so
// enter/exit of edit mode round-trips cleanly.
export function applyTextTransform(raw: string, transform: TextElement['textTransform'] | undefined): string {
  if (!raw || !transform || transform === 'none') return raw;
  switch (transform) {
    case 'uppercase': return raw.toUpperCase();
    case 'lowercase': return raw.toLowerCase();
    case 'capitalize': return raw.replace(/\b\w/g, (c) => c.toUpperCase());
    default: return raw;
  }
}

export function decorationProps(decoration: TextElement['textDecoration'] | undefined) {
  return {
    underline: decoration === 'underline',
    linethrough: decoration === 'line-through',
  };
}

export function auditTextWidths(canvas: FabricCanvas) {
  const objects = canvas.getObjects();
  let fixApplied = false;
  const storeElements = useStudioStore.getState().canvasState.elements;
  for (const obj of objects) {
    if (obj instanceof Textbox) {
      const elementId = (obj as any)[ELEMENT_ID_KEY] as string;
      const existing = storeElements.find((el) => el.id === elementId) as
        | TextElement
        | undefined;
      // Phase B.5: CWT (Contain Within Textbox) elements have designer-fixed
      // dimensions per CWT/CWS Spec Lock §4. The audit pass MUST skip them
      // entirely — no width policy application, no measurement, no write-back.
      const isCWT = existing?.textFitMode === 'contain-within-textbox' ||
                    existing?.textFitMode === 'contain-within-structure';
      if (isCWT) continue;

      const intent = resolveTextIntent(existing);
      const changed = applyTextboxWidthPolicy(obj, intent);
      if (changed) {
        fixApplied = true;
        if (elementId) {
          const newWidth = obj.width;
          const newHeight = getDescenderSafeHeight(obj);
          if (
            existing &&
            (Math.abs((existing.width ?? 0) - newWidth) > 1 ||
              Math.abs((existing.height ?? 0) - newHeight) > 1)
          ) {
            useStudioStore.getState().updateElement(elementId, {
              width: newWidth,
              height: newHeight,
            } as Partial<CanvasElement>);
          }
        }
      }
    }
  }
  if (fixApplied) {
    canvas.renderAll();
  }
}
